import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Badge, Spinner } from 'react-bootstrap';
import { Search, Download, Users, Upload } from 'lucide-react';
import VisitorListItem from './VisitorListItem';
import DeleteVisitModal from './DeleteVisitModal';
import { API_ENDPOINTS } from '../../../../config/api';
import './VisitorManagement.css';
import ManualEntryForm from './ManualEntryForm';
import { createPortal } from 'react-dom';

const calculateAge = (dobString) => {
    if (!dobString) return null;
    const today = new Date();
    const birthDate = new Date(dobString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const VisitorLogCard = ({ onStatsRefresh }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [dateFilter, setDateFilter] = useState("");
    const [dobFilter, setDobFilter] = useState("");
    const [branchFilter, setBranchFilter] = useState("");
    const [ageFilterType, setAgeFilterType] = useState("all");
    const [exactAge, setExactAge] = useState("");
    const [minAge, setMinAge] = useState("");
    const [maxAge, setMaxAge] = useState("");
    const [isExporting, setIsExporting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [visitors, setVisitors] = useState([]);
    const [allVisitors, setAllVisitors] = useState([]);
    const [activeTab, setActiveTab] = useState("today"); // "today" or "entire"
    const [logMode, setLogMode] = useState("visits"); // "visits" or "visitors", only used when activeTab === "entire"
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [visitorToDelete, setVisitorToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);

    // Get today's date in Indian Standard Time (IST) YYYY-MM-DD
    const getISTDateString = () => {
        const d = new Date();
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).formatToParts(d);

        const year = parts.find(p => p.type === 'year').value;
        const month = parts.find(p => p.type === 'month').value;
        const day = parts.find(p => p.type === 'day').value;

        return `${year}-${month}-${day}`;
    };

    // Fetch visitors with debounced search and active tab filters
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchVisitors();
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [activeTab, logMode, dateFilter, searchTerm, dobFilter, branchFilter, ageFilterType, exactAge, minAge, maxAge]);

    const fetchVisitors = async () => {
        setIsLoading(true);
        try {
            let url = API_ENDPOINTS.VISITORS.GET_ALL;
            const params = [];

            if (dateFilter) {
                params.push(`date=${dateFilter}`);
            } else if (activeTab === 'today') {
                params.push(`date=${getISTDateString()}`);
            }

            if (searchTerm.trim()) {
                params.push(`search=${encodeURIComponent(searchTerm.trim())}`);
            }
            if (dobFilter) {
                // dobFilter is stored as DD-MM, backend needs MM-DD
                const [dd, mm] = dobFilter.split('-');
                params.push(`dob=${mm}-${dd}`);
            }
            if (branchFilter) {
                params.push(`branch=${encodeURIComponent(branchFilter)}`);
            }
            if (ageFilterType === "exact" && exactAge) {
                params.push(`age=${exactAge}`);
            } else if (ageFilterType === "range") {
                if (minAge) params.push(`minAge=${minAge}`);
                if (maxAge) params.push(`maxAge=${maxAge}`);
            }

            if (activeTab === 'entire' && (logMode === 'visitors' || logMode === 'incomplete'))  {
                params.push('mode=visitors');
            }

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await fetch(url);
            const data = await response.json();
            setVisitors(data);
            setAllVisitors(data);

            // Fetch ALL visitors (no filters) to use as group member lookup source
            const allResponse = await fetch(API_ENDPOINTS.VISITORS.GET_ALL);
            const allData = await allResponse.json();
            setAllVisitors(allData);
        } catch (err) {
            console.error('Error fetching visitors:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = (visitor) => {
        setVisitorToDelete(visitor);
        setShowDeleteModal(true);
    };

    const handleDeleteVisit = (visitId, visitDate, visitorName) => {
        setVisitorToDelete({ visitId, visitDate, fullName: visitorName });
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async (deletionReason) => {
        if (!visitorToDelete || !visitorToDelete.visitId) {
            alert("This record cannot be deleted (missing visit ID).");
            return;
        }

        setIsDeleting(true);
        try {
            const response = await fetch(API_ENDPOINTS.VISITORS.DELETE_VISIT(visitorToDelete.visitId), {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    deletionReason,
                    deletedBy: 'Admin'
                })
            });

            if (response.ok) {
                setShowDeleteModal(false);
                setVisitorToDelete(null);
                fetchVisitors();
                if (onStatsRefresh) onStatsRefresh();
            } else {
                const error = await response.json();
                alert(`Error: ${error.message}`);
            }
        } catch (err) {
            console.error('Delete failed:', err);
            alert("A network error occurred. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const filteredVisitors = (activeTab === 'entire' && logMode === 'incomplete')
        ? visitors.filter(v => v.isProfileComplete === false)
        : visitors;
    const handleImportExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const response = await fetch(`${BASE_URL}/api/visitors/import-excel`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                alert(`✅ Import successful!\n✔ New visitors: ${data.imported}\n🔄 Existing visitors updated: ${data.updatedExisting}\n⚠ Skipped: ${data.skipped} rows${data.errors.length > 0 ? '\n\nErrors:\n' + data.errors.join('\n') : ''}`);
                fetchVisitors();
                if (onStatsRefresh) onStatsRefresh();
            } else {
                alert(`❌ Import failed: ${data.message}`);
            }
        } catch (err) {
            alert('❌ Network error during import. Please try again.');
        } finally {
            setIsImporting(false);
            e.target.value = '';
        }
    };
    // Export CSV with rich details
    const handleExportCSV = () => {
        setIsExporting(true);
        try {
            const headers = [
                'Name', 'Mobile', 'Email', 'Gender', 'Age',
                'Area/Locality', 'City', 'State', 'Purpose',
                'Total Visits', 'Visit Date', 'Visit Time'
            ];

            const rows = visitors.map(v => [
                v.fullName || '',
                v.mobileNumber || '',
                v.email || '',
                v.gender || '',
                v.dob ? `${calculateAge(v.dob)}` : (v.ageGroup || ''),
                v.area || '',
                v.city || '',
                v.state || '',
                v.purposeOfVisit || '',
                v.totalVisits || '0',
                v.visitDate || '',
                v.visitTime || ''
            ]);

            const csvContent = [headers, ...rows]
                .map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
                .join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${activeTab === 'today' ? 'todays' : 'entire'}_visitors_${getISTDateString()}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setIsExporting(false);
        }
    };

    // Render visitor items — grouping by groupId only in "visits" mode
    const renderVisitorItems = () => {
        if (logMode === 'visitors') {
            return filteredVisitors.map(visitor => (
                <VisitorListItem
                    key={visitor.id}
                    visitor={visitor}
                    onDelete={handleDeleteClick}
                    showGroupBadges={false}
                    onDeleteVisit={handleDeleteVisit}
                />
            ));
        }

        // Visits mode: group visits sharing the same groupId, leave individual visits flat
        const renderedGroups = new Set();
        const items = [];

        filteredVisitors.forEach((visitor) => {
            if (!visitor.isGroupVisit || !visitor.groupId) {
                items.push(
                    <VisitorListItem
                        key={visitor.id}
                        visitor={visitor}
                        onDelete={handleDeleteClick}
                        onDeleteVisit={handleDeleteVisit}
                    />
                );
            } else {
                if (renderedGroups.has(visitor.groupId)) return;
                renderedGroups.add(visitor.groupId);

                const seen = new Set();
                const groupMembersList = filteredVisitors.filter(v => {
                    if (v.groupId === visitor.groupId && !seen.has(v.visitorId)) {
                        seen.add(v.visitorId);
                        return true;
                    }
                    return false;
                });

                items.push(
                    <div key={visitor.groupId} className="db1-group-box p-3 mb-3">
                        <div className="d-flex align-items-center mb-3 px-1">
                            <span className="me-2" style={{ fontSize: '1.2rem' }}>👥</span>
                            <h6 className="mb-0 fw-bold" style={{ color: '#ac2e62', letterSpacing: '-0.2px' }}>
                                Group Visit Log ({groupMembersList.length} members)
                            </h6>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            {groupMembersList.map(member => (
                                <VisitorListItem
                                    key={member.id}
                                    visitor={member}
                                    onDelete={handleDeleteClick}
                                    onDeleteVisit={handleDeleteVisit}
                                />
                            ))}
                        </div>
                    </div>
                );
            }
        });

        return items;
    };

    return (
        <div className="db1-glass-card mt-4">
        {/* <div className="db1-glass-card mt-4" style={{ overflow: 'visible', position: 'relative' }}> */}
            <div className="px-4 pt-4 pb-2">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                        <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: 'rgba(172,46,98,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Users size={20} style={{ color: '#ac2e62' }} />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold" style={{ color: '#2c3e50', fontSize: '1.05rem' }}>Visitor Log</h5>
                        </div>
                        <span className="db1-badge" style={{ background: 'rgba(172,46,98,0.06)', color: '#ac2e62' }}>
                            {filteredVisitors.length} records
                        </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            onClick={() => setShowManualEntry(true)}
                            style={{
                                backgroundColor: '#ac2e62', border: 'none',
                                borderRadius: '10px', padding: '8px 16px',
                                fontWeight: '600', fontSize: '13px', color: 'white'
                            }}
                        >
                            + Manual Entry
                        </Button>
                        {/* Import Excel Button */}
                        <label className="db1-export-btn d-flex align-items-center gap-2" style={{ cursor: isImporting ? 'not-allowed' : 'pointer', margin: 0 }}>
                            {isImporting
                                ? <Spinner animation="border" size="sm" />
                                : <Upload size={15} />}
                            Import Excel
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                style={{ display: 'none' }}
                                onChange={handleImportExcel}
                                disabled={isImporting}
                            />
                        </label>
                        <button
                            className="db1-export-btn d-flex align-items-center gap-2"
                            onClick={handleExportCSV}
                            disabled={isExporting || visitors.length === 0}
                            style={{ cursor: isExporting || visitors.length === 0 ? 'not-allowed' : 'pointer' }}
                        >
                            {isExporting
                                ? <Spinner animation="border" size="sm" />
                                : <Download size={15} />}
                            Export CSV
                        </button>
                    </div>
                </div>
            </div>

            {/* Custom Tabs */}
            <div className="d-flex px-4" style={{ borderBottom: '1px solid rgba(172,46,98,0.06)' }}>
                <button
                    onClick={() => { setActiveTab("today"); setSearchTerm(""); setDateFilter(""); setDobFilter(""); setBranchFilter(""); setLogMode("visits"); setAgeFilterType("all"); setExactAge(""); setMinAge(""); setMaxAge(""); }}
                    className={`db1-tab-btn ${activeTab === 'today' ? 'active' : 'inactive'}`}
                >
                    Today's Visitors
                </button>
                <button
                    onClick={() => { setActiveTab("entire"); setLogMode("visits"); setSearchTerm(""); setDateFilter(""); setDobFilter(""); setBranchFilter(""); setAgeFilterType("all"); setExactAge(""); setMinAge(""); setMaxAge(""); }}
                    className={`db1-tab-btn ${activeTab === 'entire' && logMode === 'visits' ? 'active' : 'inactive'}`}
                >
                    Entire Visits Log
                </button>
                <button
                    onClick={() => { setActiveTab("entire"); setLogMode("visitors"); setSearchTerm(""); setDateFilter(""); setDobFilter(""); setBranchFilter(""); setAgeFilterType("all"); setExactAge(""); setMinAge(""); setMaxAge(""); }}
                    className={`db1-tab-btn ${activeTab === 'entire' && logMode === 'visitors' ? 'active' : 'inactive'}`}
                >
                    Entire Visitors Log
                </button>
                <button
                    onClick={() => { setActiveTab("entire"); setLogMode("incomplete"); setSearchTerm(""); setDateFilter(""); setDobFilter(""); setBranchFilter(""); setAgeFilterType("all"); setExactAge(""); setMinAge(""); setMaxAge(""); }}
                    className={`db1-tab-btn ${activeTab === 'entire' && logMode === 'incomplete' ? 'active' : 'inactive'}`}
                >
                    Incomplete Profiles
                </button>
            </div>

            <div className="p-4">
                {/* Search & Filter */}
                <div className="d-flex flex-wrap gap-3 mb-4">
                    <div className="flex-grow-1 position-relative">
                        <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#7f8c9b', zIndex: 2 }}>
                            <Search size={18} />
                        </span>
                        <Form.Control
                            type="text"
                            placeholder="Search by name, email, phone, city, causes..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="db1-search"
                        />
                    </div>
                    {/* {activeTab === "entire" && ( */}
                        <div className="d-flex gap-2 align-items-center">
                            <Form.Control
                                type="date"
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                className="db1-date-input"
                                style={{ width: '160px' }}
                            />
                            {dateFilter && (
                                <button
                                    onClick={() => setDateFilter("")}
                                    style={{
                                        borderRadius: '999px',
                                        height: '46px',
                                        padding: '0 18px',
                                        border: '1px solid rgba(172,46,98,0.10)',
                                        background: 'rgba(255,255,255,0.6)',
                                        color: '#7f8c9b',
                                        fontSize: '0.82rem',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    title="Reset to Overall"
                                >
                                    Reset
                                </button>
                            )}
                            {/* DOB Filter */}
                            <div className="d-flex align-items-center gap-1">
                                <div 
                                    style={{ borderRadius: '10px', height: '45px', border: '1px solid #eee', width: '155px', backgroundColor: 'white', display: 'flex', alignItems: 'center', paddingLeft: '12px', position: 'relative', cursor: 'pointer' }}
                                    onClick={() => document.getElementById('dobPicker').showPicker()}
                                >
                                    <span style={{ fontSize: '0.85rem', color: dobFilter ? '#2c3e50' : '#aaa', flex: 1 }}>
                                        {dobFilter ? dobFilter : 'DOB (DD-MM)'}
                                    </span>
                                    <span style={{ paddingRight: '10px', color: '#aaa' }}>📅</span>
                                    <input
                                        id="dobPicker"
                                        type="date"
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                const parts = e.target.value.split('-');
                                                const day = parts[2].padStart(2, '0');
                                                const month = parts[1].padStart(2, '0');
                                                setDobFilter(`${day}-${month}`); // DD-MM format
                                            } else {
                                                setDobFilter("");
                                            }
                                        }}
                                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                                    />
                                </div>
                                {dobFilter && (
                                    <Button variant="link" className="p-0 text-muted" onClick={() => setDobFilter("")} style={{ fontSize: '0.8rem' }}>✕</Button>
                                )}
                            </div>

                            {/* Branch Filter */}
                            <div className="d-flex align-items-center gap-1">
                                <Form.Select
                                    value={branchFilter}
                                    onChange={(e) => setBranchFilter(e.target.value)}
                                    style={{ borderRadius: '10px', height: '45px', border: '1px solid #eee', width: '145px', fontSize: '0.85rem' }}
                                >
                                    <option value="">All Branches</option>
                                    <option value="Maainagari">Maainagari</option>
                                    <option value="Shree Manashanti Chhatralaya">Shree Manashanti Chhatralaya</option>
                                    <option value="TMGF Central Office">TMGF Central Office</option>
                                    <option value="Maainagari - Solapur">Maainagari - Solapur</option>
                                    <option value="Maainagari - Goa">Maainagari - Goa</option>
                                </Form.Select>
                                {branchFilter && (
                                    <Button variant="link" className="p-0 text-muted" onClick={() => setBranchFilter("")} style={{ fontSize: '0.8rem' }}>✕</Button>
                                )}
                            </div>

                            {/* Age Filter */}
                            <div className="d-flex align-items-center gap-2">
                                <Form.Select
                                    value={ageFilterType}
                                    onChange={(e) => {
                                        setAgeFilterType(e.target.value);
                                        setExactAge("");
                                        setMinAge("");
                                        setMaxAge("");
                                    }}
                                    style={{ borderRadius: '10px', height: '45px', border: '1px solid #eee', width: '120px', fontSize: '0.85rem' }}
                                >
                                    <option value="all">All Ages</option>
                                    <option value="exact">Exact Age</option>
                                    <option value="range">Age Range</option>
                                </Form.Select>

                                {ageFilterType === "exact" && (
                                    <Form.Control
                                        type="number"
                                        placeholder="Age"
                                        value={exactAge}
                                        onChange={(e) => setExactAge(e.target.value)}
                                        style={{ borderRadius: '10px', height: '45px', border: '1px solid #eee', width: '80px', fontSize: '0.85rem' }}
                                        min="0"
                                        max="120"
                                    />
                                )}

                                {ageFilterType === "range" && (
                                    <div className="d-flex align-items-center gap-1">
                                        <Form.Control
                                            type="number"
                                            placeholder="Min"
                                            value={minAge}
                                            onChange={(e) => setMinAge(e.target.value)}
                                            style={{ borderRadius: '10px', height: '45px', border: '1px solid #eee', width: '70px', fontSize: '0.85rem' }}
                                            min="0"
                                            max="120"
                                        />
                                        <span style={{ fontSize: '0.8rem', color: '#7f8c9b' }}>-</span>
                                        <Form.Control
                                            type="number"
                                            placeholder="Max"
                                            value={maxAge}
                                            onChange={(e) => setMaxAge(e.target.value)}
                                            style={{ borderRadius: '10px', height: '45px', border: '1px solid #eee', width: '70px', fontSize: '0.85rem' }}
                                            min="0"
                                            max="120"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    {/* )} */}
                </div>

                {/* Visitor List */}
                <div className="visitor-list-container">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <div style={{ display: 'inline-block', width: '32px', height: '32px', borderRadius: '50%', border: '3px solid rgba(172,46,98,0.08)', borderTopColor: '#ac2e62', animation: 'spin 0.6s linear infinite' }} />
                            <p className="mt-2" style={{ color: '#7f8c9b', fontSize: '0.9rem' }}>Loading visitors...</p>
                        </div>
                    ) : filteredVisitors.length > 0 ? (
                        activeTab === 'entire' && logMode === 'incomplete'
                            ? filteredVisitors.map(visitor => (
                                <VisitorListItem
                                key={visitor.id || visitor.visitorId}
                                visitor={visitor}
                                onDelete={handleDeleteClick}
                                showGroupBadges={false}
                            />
                            ))
                            : renderVisitorItems()
                    ) : (
                        <div className="db1-empty-state text-center">
                            <Users size={52} style={{ color: '#7f8c9b', opacity: 0.2, marginBottom: '12px' }} />
                            <h6 style={{ color: '#7f8c9b', marginBottom: '6px' }}>No visitors found</h6>
                            <p style={{ color: '#7f8c9b', fontSize: '0.85rem', marginBottom: 0, opacity: 0.7 }}>
                                {activeTab === "today"
                                    ? "No visitors have registered or checked in today."
                                    : dateFilter
                                        ? "No visitor logs found for this selected date."
                                        : logMode === 'visitors'
                                            ? "No visitors found."
                                            : "No visitor logs available."
                                }
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <DeleteVisitModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                visitorName={visitorToDelete?.fullName}
                visitDate={visitorToDelete?.visitDate}
                isDeleting={isDeleting}
            />
            {/* Manual Entry Modal */}
            {showManualEntry && createPortal(
            <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(240, 242, 245, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            zIndex: 999999, overflowY: 'auto',
            margin: 0, padding: 0
        }}>
        <div style={{ maxWidth: '750px', margin: '0 auto', padding: '50px 24px' }}>
            <button onClick={() => setShowManualEntry(false)}
                style={{
                    background: 'white', border: '1px solid #ddd', borderRadius: '10px',
                    padding: '8px 18px', fontWeight: '600', fontSize: '14px',
                    color: '#555', marginBottom: '24px', cursor: 'pointer'
                }}>
                ← Back to Dashboard
            </button>

            <div style={{
                background: 'white', borderRadius: '20px', padding: '40px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
            }}>
                <h2 style={{ color: '#2c3e50', fontWeight: '800', marginBottom: '8px' }}>
                    Manual Visitor Entry
                </h2>
                <p style={{ color: '#888', marginBottom: '30px' }}>
                    Add a visitor entry with a custom date and time.
                </p>

                <ManualEntryForm
                    onSuccess={() => {
                        setShowManualEntry(false);
                        fetchVisitors();
                    }}
                    onCancel={() => setShowManualEntry(false)}
                />
            </div>
        </div>
    </div>,
    document.body
)}
        </div>
    );
};

export default VisitorLogCard;